import { Log } from '../typegen/log';
import { ISqliteService } from '../types/SqliteServiceType';
import KioAppHelper from './kioApp';
import '../types/KioFileIO';
import logger from './logger';
import ApiCall, { ApiCallServiceType } from '../types/ApiCall';
import BillInsertion from '../types/BillInsertion';
import PendingCashPayment from '../types/PendingCashPayment';
import { ulid } from 'ulid';
import { v4 as guid } from 'uuid';
import CashPaymentData from '../types/CashPaymentData';

type SqliteDataType = string | number | boolean | null;

interface SqliteParams {
	[key: string]: SqliteDataType;
}

interface SqliteRow {
	[key: string]: SqliteDataType;
}

declare global {
	interface Window {
		KioSqLite: {
			Open: (connStr: string) => number;
			Close_Async: (connStr: string) => Promise<void>;
			Execute_Async: (connId: number, useTransaction: boolean, sql: string | null, parameters: SqliteParams | null) => Promise<number>;
			ExecuteScalar_Async: (
				connId: number,
				useTransaction: boolean,
				sql: string | null,
				parameters: SqliteParams | null
			) => Promise<SqliteDataType>;
			BeginQuery_Async: (connId: number, useTransaction: boolean, sql: string | null, parameters: SqliteParams | null) => Promise<void>;
			GetRow_Async: (connId: number) => Promise<SqliteRow | null>;
			EndQuery_Async: (connId: number) => Promise<void>;
			Query_Async: (connId: number, useTransaction: boolean, sql: string | null, parameters: SqliteParams | null) => Promise<SqliteRow[]>;
			BeginTransaction_Async: (connId: number) => Promise<void>;
			Commit_Async: (connId: number) => Promise<void>;
			Rollback_Async: (connId: number) => Promise<void>;
			ReleaseTransLock: (connId: number) => void;
		};
		KioEncryption: {
			E2sPdCrc32B64: (v: string, minPd: number, maxPd: number) => string;
			D2sPdCrc32B64: (v: string) => string;
		};
		startService: () => void;
	}
}

export class SqliteService implements ISqliteService {
	private connId: number = -1;
	private connStr: string = '';
	private transactionQueue: Promise<void> = Promise.resolve();

	async releaseTransLock(): Promise<void> {
		try {
			window.KioSqLite.ReleaseTransLock(this.connId);
		} catch {
			logger.debug.error('Failed to release transaction lock on SQLite database');
		}
	}

	async init(): Promise<void> {
		this.transactionQueue = this.transactionQueue.then(async () => {
			if (this.connId !== -1) return;

			const dataSource = KioAppHelper('DoUrlSubstitution', import.meta.env.VITE_SQLITE_DATA_SOURCE)?.replace('file:///', '');
			this.connStr = `Data Source=${dataSource};Pooling=False;`;
			this.connId = window.KioSqLite.Open(this.connStr);
			if (this.connId === -1) throw new Error('Failed to open SQLite database connection.');

			await window.KioSqLite.Execute_Async(this.connId, false, 'PRAGMA journal_mode=WAL;', {});
			await window.KioSqLite.Execute_Async(this.connId, false, 'PRAGMA foreign_keys=ON;', {});

			await window.KioSqLite.Execute_Async(
				this.connId,
				false,
				`
					CREATE TABLE IF NOT EXISTS tbl_logs (
						id TEXT PRIMARY KEY,
						timestamp TEXT NOT NULL,
						logData TEXT NOT NULL
					);
				`,
				null
			);

			await window.KioSqLite.Execute_Async(
				this.connId,
				false,
				`
					CREATE TABLE IF NOT EXISTS tbl_apicalls (
						id TEXT PRIMARY KEY,
						timestamp TEXT NOT NULL,
						type INTEGER NOT NULL,
						requestData TEXT
					);
				`,
				null
			);

			await window.KioSqLite.Execute_Async(
				this.connId,
				false,
				`
					CREATE TABLE IF NOT EXISTS tbl_settings (
						key TEXT PRIMARY KEY,
						value TEXT NOT NULL
					);
				`,
				null
			);

			await window.KioSqLite.Execute_Async(
				this.connId,
				false,
				`
					INSERT OR IGNORE INTO tbl_settings (key, value)
					VALUES
						(@key1, @value1),
						(@key2, @value2);
				`,
				{
					key1: 'cashBoxAlarmEnabled',
					value1: 'true',
					key2: 'cashBoxAlarmTriggered',
					value2: 'false',
				}
			);

			await window.KioSqLite.Execute_Async(
				this.connId,
				false,
				`
					CREATE TABLE IF NOT EXISTS tbl_pending_cash_payments (
						id TEXT PRIMARY KEY,
						sessionId TEXT NOT NULL,
						companyId INTEGER NOT NULL,
						kioskId INTEGER NOT NULL,
						customerName TEXT NOT NULL,
						utilityAccountNumber TEXT NOT NULL,
						amountDue REAL NOT NULL,
						invoiceGuid TEXT,
						paymentTypeId INTEGER,
						legacyGuid TEXT NOT NULL,
						cisId TEXT NOT NULL,
						paymentGatewayId TEXT NOT NULL,
						cisProviderName TEXT NOT NULL,
						paymentGatewayName TEXT NOT NULL,
						paymentGuid TEXT,
						paymentGatewayExternalId TEXT
					);
				`,
				null
			);

			try {
				await window.KioSqLite.Execute_Async(this.connId, false, 'ALTER TABLE tbl_pending_cash_payments ADD COLUMN paymentGuid TEXT;', null);
			} catch {
				// Column already exists on previously-initialized databases.
			}

			try {
				await window.KioSqLite.Execute_Async(
					this.connId,
					false,
					'ALTER TABLE tbl_pending_cash_payments ADD COLUMN paymentGatewayExternalId TEXT;',
					null
				);
			} catch {
				// Column already exists on previously-initialized databases.
			}

			await window.KioSqLite.Execute_Async(
				this.connId,
				false,
				`
					CREATE TABLE IF NOT EXISTS tbl_bill_insertions (
						id TEXT PRIMARY KEY,
						pendingCashPaymentId TEXT NOT NULL,
						amount REAL NOT NULL,
						FOREIGN KEY (pendingCashPaymentId) REFERENCES tbl_pending_cash_payments(id)
							ON DELETE CASCADE
					);
				`,
				null
			);

			KioAppHelper(
				'InjectJavaScript',
				'if(window.startService){window.startService();}',
				null,
				true,
				KioBrowserWndType.Main | KioBrowserWndType.Popup | KioBrowserWndType.Secondary,
				KioBrowserType.All
			);
		});

		return this.transactionQueue;
	}

	async close(): Promise<void> {
		if (this.connId === -1) return;

		this.transactionQueue = this.transactionQueue
			.then(async () => {
				await window.KioSqLite.Close_Async(this.connStr);
				this.connId = -1;
				this.connStr = '';
			})
			.catch((error) => {
				logger.debug.error('Failed to close SQLite database connection', error instanceof Error ? error.message : error);
			});

		return this.transactionQueue;
	}

	async createLog(log: Log): Promise<void> {
		this.transactionQueue = this.transactionQueue
			.then(async () => {
				if (this.connId === -1) throw new Error('SQLite connection is not initialized.');

				await window.KioSqLite.BeginTransaction_Async(this.connId);
				await window.KioSqLite.Execute_Async(
					this.connId,
					true,
					`
						INSERT INTO tbl_logs (id, timestamp, logData)
						VALUES (@id, @timestamp, @logData);
					`,
					{
						id: log.id,
						timestamp: log.createdAt.toISOString(),
						logData: window.KioEncryption.E2sPdCrc32B64(JSON.stringify(log), 0, 0),
					}
				);
				await window.KioSqLite.Commit_Async(this.connId);
			})
			.catch(async (error) => {
				logger.debug.error('Failed to create log in SQLite database', error instanceof Error ? error.message : error);
				await this.releaseTransLock();
			});

		return this.transactionQueue;
	}

	async beginQueryLogs(): Promise<void> {
		this.transactionQueue = this.transactionQueue
			.then(async () => {
				if (this.connId === -1) throw new Error('SQLite connection is not initialized.');
				await window.KioSqLite.BeginQuery_Async(this.connId, false, 'SELECT * FROM tbl_logs ORDER BY timestamp ASC;', null);
			})
			.catch((error) => {
				logger.debug.error('Failed to begin querying logs in SQLite database', error instanceof Error ? error.message : error);
			});

		return this.transactionQueue;
	}

	async getNextLog(): Promise<Log | null> {
		let log: Log | null = null;
		this.transactionQueue = this.transactionQueue
			.then(async () => {
				if (this.connId === -1) throw new Error('SQLite connection is not initialized.');
				const row = await window.KioSqLite.GetRow_Async(this.connId);
				log = !row ? null : (JSON.parse(window.KioEncryption.D2sPdCrc32B64(row.logData as string)) as Log);
			})
			.catch((error) => {
				logger.debug.error('Failed to get next log from SQLite database', error instanceof Error ? error.message : error);
				log = null;
			});

		await this.transactionQueue;
		return log;
	}

	async endQueryLogs(): Promise<void> {
		this.transactionQueue = this.transactionQueue
			.then(async () => {
				if (this.connId === -1) throw new Error('SQLite connection is not initialized.');
				await window.KioSqLite.EndQuery_Async(this.connId);
			})
			.catch((error) => {
				logger.debug.error('Failed to end querying logs in SQLite database', error instanceof Error ? error.message : error);
			});

		return this.transactionQueue;
	}

	async deleteLog(log: Log): Promise<void> {
		this.transactionQueue = this.transactionQueue
			.then(async () => {
				if (this.connId === -1) throw new Error('SQLite connection is not initialized.');

				await window.KioSqLite.BeginTransaction_Async(this.connId);
				await window.KioSqLite.Execute_Async(
					this.connId,
					true,
					`
						DELETE FROM tbl_logs WHERE id = @id;
					`,
					{
						id: log.id,
					}
				);
				await window.KioSqLite.Commit_Async(this.connId);
			})
			.catch(async (error) => {
				logger.debug.error('Failed to delete logs from SQLite database', error instanceof Error ? error.message : error);
				await this.releaseTransLock();
			});

		return this.transactionQueue;
	}

	async createApiCall(apiCall: ApiCall): Promise<void> {
		this.transactionQueue = this.transactionQueue
			.then(async () => {
				if (this.connId === -1) throw new Error('SQLite connection is not initialized.');
				await window.KioSqLite.BeginTransaction_Async(this.connId);
				await window.KioSqLite.Execute_Async(
					this.connId,
					true,
					`
						INSERT INTO tbl_apicalls (id, timestamp, type, requestData)
						VALUES (@id, @timestamp, @type, @requestData);
					`,
					{
						id: apiCall.id,
						timestamp: apiCall.timestamp.toISOString(),
						type: apiCall.type,
						requestData: window.KioEncryption.E2sPdCrc32B64(apiCall.requestData, 0, 0),
					}
				);
				await window.KioSqLite.Commit_Async(this.connId);
			})
			.catch(async (error) => {
				logger.debug.error('Failed to create API call in SQLite database', error instanceof Error ? error.message : error);
				await this.releaseTransLock();
			});

		return this.transactionQueue;
	}

	async beginQueryApiCalls(): Promise<void> {
		this.transactionQueue = this.transactionQueue
			.then(async () => {
				if (this.connId === -1) throw new Error('SQLite connection is not initialized.');
				await window.KioSqLite.BeginQuery_Async(this.connId, false, 'SELECT * FROM tbl_apicalls ORDER BY timestamp ASC;', null);
			})
			.catch((error) => {
				logger.debug.error('Failed to begin querying API calls in SQLite database', error instanceof Error ? error.message : error);
			});

		return this.transactionQueue;
	}

	async getNextApiCall(): Promise<ApiCall | null> {
		let apiCall: ApiCall | null = null;
		this.transactionQueue = this.transactionQueue
			.then(async () => {
				if (this.connId === -1) throw new Error('SQLite connection is not initialized.');
				const row = await window.KioSqLite.GetRow_Async(this.connId);
				apiCall = !row
					? null
					: {
							id: row.id as string,
							timestamp: new Date(row.timestamp as string),
							type: row.type as number,
							requestData: window.KioEncryption.D2sPdCrc32B64(row.requestData as string),
						};
			})
			.catch((error) => {
				logger.debug.error('Failed to get next API call from SQLite database', error instanceof Error ? error.message : error);
				apiCall = null;
			});

		await this.transactionQueue;
		return apiCall;
	}

	async endQueryApiCalls(): Promise<void> {
		this.transactionQueue = this.transactionQueue
			.then(async () => {
				if (this.connId === -1) throw new Error('SQLite connection is not initialized.');
				await window.KioSqLite.EndQuery_Async(this.connId);
			})
			.catch((error) => {
				logger.debug.error('Failed to end querying API calls in SQLite database', error instanceof Error ? error.message : error);
			});

		return this.transactionQueue;
	}

	async deleteApiCall(apiCall: ApiCall): Promise<void> {
		this.transactionQueue = this.transactionQueue
			.then(async () => {
				if (this.connId === -1) throw new Error('SQLite connection is not initialized.');

				await window.KioSqLite.BeginTransaction_Async(this.connId);
				await window.KioSqLite.Execute_Async(
					this.connId,
					true,
					`
						DELETE FROM tbl_apicalls WHERE id = @id;
					`,
					{
						id: apiCall.id,
					}
				);
				await window.KioSqLite.Commit_Async(this.connId);
			})
			.catch(async (error) => {
				logger.debug.error('Failed to delete API call from SQLite database', error instanceof Error ? error.message : error);
				await this.releaseTransLock();
			});

		return this.transactionQueue;
	}

	async getSetting(key: string): Promise<string | null> {
		let value: string | null = null;
		this.transactionQueue = this.transactionQueue
			.then(async () => {
				if (this.connId === -1) throw new Error('SQLite connection is not initialized.');

				const result = await window.KioSqLite.ExecuteScalar_Async(this.connId, false, 'SELECT value FROM tbl_settings WHERE key = @key;', {
					key: key,
				});
				value = !result ? null : (result as string);
			})
			.catch((error) => {
				logger.debug.error('Failed to get setting from SQLite database', error instanceof Error ? error.message : error);
				value = null;
			});

		await this.transactionQueue;
		return value;
	}

	async tryGetSetting(key: string): Promise<{ value: string | null; failed: boolean }> {
		let result: { value: string | null; failed: boolean } = { value: null, failed: false };
		this.transactionQueue = this.transactionQueue
			.then(async () => {
				if (this.connId === -1) throw new Error('SQLite connection is not initialized.');

				const row = await window.KioSqLite.ExecuteScalar_Async(this.connId, false, 'SELECT value FROM tbl_settings WHERE key = @key;', {
					key: key,
				});
				result = { value: !row ? null : (row as string), failed: false };
			})
			.catch((error) => {
				logger.debug.error('Failed to read setting from SQLite database', error instanceof Error ? error.message : error);
				result = { value: null, failed: true };
			});

		await this.transactionQueue;
		return result;
	}

	async setSetting(key: string, value: string): Promise<void> {
		this.transactionQueue = this.transactionQueue
			.then(async () => {
				if (this.connId === -1) throw new Error('SQLite connection is not initialized.');

				await window.KioSqLite.BeginTransaction_Async(this.connId);
				await window.KioSqLite.Execute_Async(
					this.connId,
					true,
					`
						INSERT INTO tbl_settings (key, value)
						VALUES (@key, @value)
						ON CONFLICT(key) DO UPDATE SET value = @value;
					`,
					{
						key: key,
						value: value,
					}
				);
				await window.KioSqLite.Commit_Async(this.connId);
			})
			.catch(async (error) => {
				logger.debug.error('Failed to set setting in SQLite database', error instanceof Error ? error.message : error);
				await this.releaseTransLock();
			});

		return this.transactionQueue;
	}

	async createPendingCashPayment(pendingCashPayment: PendingCashPayment): Promise<void> {
		this.transactionQueue = this.transactionQueue
			.then(async () => {
				if (this.connId === -1) throw new Error('SQLite connection is not initialized.');

				await window.KioSqLite.BeginTransaction_Async(this.connId);
				await window.KioSqLite.Execute_Async(
					this.connId,
					true,
					`
						INSERT INTO tbl_pending_cash_payments (
							id,
							sessionId,
							companyId,
							kioskId,
							customerName,
							utilityAccountNumber,
							amountDue,
							invoiceGuid,
							paymentTypeId,
							legacyGuid,
							cisId,
							paymentGatewayId,
							cisProviderName,
							paymentGatewayName,
							paymentGuid,
							paymentGatewayExternalId
						) VALUES (
							@id,
							@sessionId,
							@companyId,
							@kioskId,
							@customerName,
							@utilityAccountNumber,
							@amountDue,
							@invoiceGuid,
							@paymentTypeId,
							@legacyGuid,
							@cisId,
							@paymentGatewayId,
							@cisProviderName,
							@paymentGatewayName,
							@paymentGuid,
							@paymentGatewayExternalId
						);
					`,
					{
						id: pendingCashPayment.id,
						sessionId: pendingCashPayment.sessionId,
						companyId: pendingCashPayment.companyId,
						kioskId: pendingCashPayment.kioskId,
						customerName: window.KioEncryption.E2sPdCrc32B64(pendingCashPayment.customerName, 0, 0),
						utilityAccountNumber: window.KioEncryption.E2sPdCrc32B64(pendingCashPayment.utilityAccountNumber, 0, 0),
						amountDue: pendingCashPayment.amountDue,
						invoiceGuid: pendingCashPayment.invoiceGuid ?? null,
						paymentTypeId: pendingCashPayment.paymentTypeId ?? null,
						legacyGuid: pendingCashPayment.legacyGuid,
						cisId: pendingCashPayment.cisId,
						paymentGatewayId: pendingCashPayment.paymentGatewayId,
						cisProviderName: pendingCashPayment.cisProviderName,
						paymentGatewayName: pendingCashPayment.paymentGatewayName,
						paymentGuid: pendingCashPayment.paymentGuid,
						paymentGatewayExternalId: pendingCashPayment.paymentGatewayExternalId ?? null,
					}
				);
				await window.KioSqLite.Commit_Async(this.connId);
			})
			.catch(async (error) => {
				logger.debug.error('Failed to create pending cash payment in SQLite database', error instanceof Error ? error.message : error);
				await this.releaseTransLock();
			});

		return this.transactionQueue;
	}

	async deletePendingCashPayment(id: string): Promise<void> {
		this.transactionQueue = this.transactionQueue
			.then(async () => {
				if (this.connId === -1) throw new Error('SQLite connection is not initialized.');

				await window.KioSqLite.BeginTransaction_Async(this.connId);
				await window.KioSqLite.Execute_Async(
					this.connId,
					true,
					`
						DELETE FROM tbl_pending_cash_payments WHERE id = @id;
					`,
					{
						id: id,
					}
				);
				await window.KioSqLite.Commit_Async(this.connId);
			})
			.catch(async (error) => {
				logger.debug.error('Failed to delete pending cash payment from SQLite database', error instanceof Error ? error.message : error);
				await this.releaseTransLock();
			});

		return this.transactionQueue;
	}

	async createBillInsertion(billInsertion: BillInsertion): Promise<void> {
		this.transactionQueue = this.transactionQueue
			.then(async () => {
				if (this.connId === -1) throw new Error('SQLite connection is not initialized.');

				await window.KioSqLite.BeginTransaction_Async(this.connId);
				await window.KioSqLite.Execute_Async(
					this.connId,
					true,
					`
						INSERT INTO tbl_bill_insertions (id, pendingCashPaymentId, amount)
						VALUES (@id, @pendingCashPaymentId, @amount);
					`,
					{
						id: billInsertion.id,
						pendingCashPaymentId: billInsertion.pendingCashPaymentId,
						amount: billInsertion.amount,
					}
				);
				await window.KioSqLite.Commit_Async(this.connId);
			})
			.catch(async (error) => {
				logger.debug.error('Failed to create bill insertion in SQLite database', error instanceof Error ? error.message : error);
				await this.releaseTransLock();
			});

		return this.transactionQueue;
	}

	async processPendingCashPayments(sessionId: string | null): Promise<void> {
		this.transactionQueue = this.transactionQueue
			.then(async () => {
				if (this.connId === -1) throw new Error('SQLite connection is not initialized.');

				const pendingPayments = await window.KioSqLite.Query_Async(
					this.connId,
					false,
					'SELECT * FROM tbl_pending_cash_payments' + (sessionId ? ' WHERE sessionId != @sessionId;' : ';'),
					{ sessionId }
				);

				if (pendingPayments.length > 0) {
					KioAppHelper(
						'LogWarn',
						`Recovering ${pendingPayments.length} pending cash payment(s); currentSessionId=${sessionId ?? '(empty)'}, ` +
							`paymentSessionIds=${pendingPayments.map((p) => p.sessionId as string).join(',')}`
					);
				}

				for (const pendingPaymentRow of pendingPayments) {
					const pendingPayment: PendingCashPayment = {
						id: pendingPaymentRow.id as string,
						sessionId: pendingPaymentRow.sessionId as string,
						companyId: pendingPaymentRow.companyId as number,
						kioskId: pendingPaymentRow.kioskId as number,
						customerName: window.KioEncryption.D2sPdCrc32B64(pendingPaymentRow.customerName as string),
						utilityAccountNumber: window.KioEncryption.D2sPdCrc32B64(pendingPaymentRow.utilityAccountNumber as string),
						amountDue: pendingPaymentRow.amountDue as number,
						invoiceGuid: pendingPaymentRow.invoiceGuid as string,
						paymentTypeId: pendingPaymentRow.paymentTypeId as number,
						legacyGuid: pendingPaymentRow.legacyGuid as string,
						// Back-compat: rows queued before the paymentGuid column was added
						// will be NULL here. Generate one on recovery so the legacy POST and
						// the new API call share the same value.
						paymentGuid: (pendingPaymentRow.paymentGuid as string | null) ?? guid(),
						cisId: pendingPaymentRow.cisId as string,
						paymentGatewayId: pendingPaymentRow.paymentGatewayId as string,
						// Back-compat: rows queued before this column existed are NULL, so the
						// legacy post falls back to paymentGatewayId (the account number).
						paymentGatewayExternalId: (pendingPaymentRow.paymentGatewayExternalId as string | null | undefined) ?? null,
						cisProviderName: pendingPaymentRow.cisProviderName as string,
						paymentGatewayName: pendingPaymentRow.paymentGatewayName as string,
						paymentReference: pendingPaymentRow.paymentReference as string,
					};
					this.processPendingCashPayment(pendingPayment);
				}
			})
			.catch(async (error) => {
				logger.debug.error('Failed to process pending cash payments in SQLite database', error instanceof Error ? error.message : error);
			});

		return this.transactionQueue;
	}

	private async processPendingCashPayment(pendingPayment: PendingCashPayment): Promise<void> {
		this.transactionQueue = this.transactionQueue
			.then(async () => {
				if (this.connId === -1) throw new Error('SQLite connection is not initialized.');

				const billInsertions = await window.KioSqLite.Query_Async(
					this.connId,
					false,
					'SELECT * FROM tbl_bill_insertions WHERE pendingCashPaymentId = @pendingCashPaymentId;',
					{
						pendingCashPaymentId: pendingPayment.id,
					}
				);

				let amountToPay = 0;
				let ones = 0;
				let twos = 0;
				let fives = 0;
				let tens = 0;
				let twenties = 0;
				let fifties = 0;
				let hundreds = 0;
				let unknownCount = 0;
				let unknownAmount = 0;
				for (const insertion of billInsertions) {
					const amount = insertion.amount as number;
					if (amount === 1) ones++;
					else if (amount === 2) twos++;
					else if (amount === 5) fives++;
					else if (amount === 10) tens++;
					else if (amount === 20) twenties++;
					else if (amount === 50) fifties++;
					else if (amount === 100) hundreds++;
					else {
						unknownCount++;
						unknownAmount += amount;
						continue;
					}

					amountToPay += amount;
				}

				const requestData: CashPaymentData = new CashPaymentData({
					id: pendingPayment.id,
					sessionId: pendingPayment.sessionId,
					companyId: pendingPayment.companyId,
					kioskId: pendingPayment.kioskId,
					customerName: pendingPayment.customerName,
					utilityAccountNumber: pendingPayment.utilityAccountNumber,
					amountDue: pendingPayment.amountDue,
					amountToPay: amountToPay,
					invoiceGuid: pendingPayment.invoiceGuid,
					paymentTypeId: pendingPayment.paymentTypeId,
					legacyGuid: pendingPayment.legacyGuid,
					paymentGuid: pendingPayment.paymentGuid,
					cisId: pendingPayment.cisId,
					paymentGatewayId: pendingPayment.paymentGatewayId,
					paymentGatewayExternalId: pendingPayment.paymentGatewayExternalId,
					cisProviderName: pendingPayment.cisProviderName,
					paymentGatewayName: pendingPayment.paymentGatewayName,
					ones: ones,
					twos: twos,
					fives: fives,
					tens: tens,
					twenties: twenties,
					fifties: fifties,
					hundreds: hundreds,
					unknownCount: unknownCount,
					unknownAmount: unknownAmount,
					timestamp: new Date().toISOString(),
					paymentReference: pendingPayment.paymentReference,
				});

				const apiCall: ApiCall = {
					id: ulid().toUpperCase(),
					timestamp: new Date(),
					type: ApiCallServiceType.PostCashPayment,
					requestData: JSON.stringify(requestData),
				};

				await window.KioSqLite.BeginTransaction_Async(this.connId);
				if (amountToPay > 0) {
					await window.KioSqLite.Execute_Async(
						this.connId,
						true,
						`
								INSERT INTO tbl_apicalls (id, timestamp, type, requestData)
								VALUES (@id, @timestamp, @type, @requestData);
							`,
						{
							id: apiCall.id,
							timestamp: apiCall.timestamp.toISOString(),
							type: apiCall.type,
							requestData: window.KioEncryption.E2sPdCrc32B64(apiCall.requestData, 0, 0),
						}
					);
				}

				await window.KioSqLite.Execute_Async(
					this.connId,
					true,
					`
							DELETE FROM tbl_pending_cash_payments WHERE id = @id;
						`,
					{
						id: pendingPayment.id as string,
					}
				);

				await window.KioSqLite.Commit_Async(this.connId);
				logger.debug.log('Processed pending cash payment:', pendingPayment);
			})
			.catch(async (error) => {
				logger.debug.error('Failed to process pending cash payment from SQLite database', error instanceof Error ? error.message : error);
				await this.releaseTransLock();
			});

		return this.transactionQueue;
	}
}

export class SqliteServiceStub implements ISqliteService {
	private logs: Log[] = [];
	private apiCalls: ApiCall[] = [];
	private pendingCashPayments: PendingCashPayment[] = [];
	private billInsertions: BillInsertion[] = [];
	private logQueryIndex: number = 0;
	private apiCallQueryIndex: number = 0;
	private settings: { [key: string]: string } = {};

	async init(): Promise<void> {}

	async close(): Promise<void> {}

	async createLog(log: Log): Promise<void> {
		this.logs.push(log);
	}

	async beginQueryLogs(): Promise<void> {
		this.logQueryIndex = 0;
	}

	async getNextLog(): Promise<Log | null> {
		if (this.logQueryIndex >= this.logs.length) return null;
		const log = this.logs[this.logQueryIndex];
		this.logQueryIndex++;
		return log;
	}

	async endQueryLogs(): Promise<void> {
		this.logQueryIndex = 0;
	}

	async deleteLog(log: Log): Promise<void> {
		this.logs = this.logs.filter((l) => l !== log);
	}

	async createApiCall(apiCall: ApiCall): Promise<void> {
		this.apiCalls.push(apiCall);
	}

	async beginQueryApiCalls(): Promise<void> {
		this.apiCallQueryIndex = 0;
	}

	async getNextApiCall(): Promise<ApiCall | null> {
		if (this.apiCallQueryIndex >= this.apiCalls.length) return null;
		const apiCall = this.apiCalls[this.apiCallQueryIndex];
		this.apiCallQueryIndex++;
		return apiCall;
	}

	async endQueryApiCalls(): Promise<void> {
		this.apiCallQueryIndex = 0;
	}

	async deleteApiCall(apiCall: ApiCall): Promise<void> {
		this.apiCalls = this.apiCalls.filter((a) => a !== apiCall);
	}

	async getSetting(key: string): Promise<string | null> {
		return this.settings[key] || null;
	}

	async tryGetSetting(key: string): Promise<{ value: string | null; failed: boolean }> {
		return { value: this.settings[key] || null, failed: false };
	}

	async setSetting(key: string, value: string): Promise<void> {
		this.settings[key] = value;
	}

	async createPendingCashPayment(pendingCashPayment: PendingCashPayment): Promise<void> {
		this.pendingCashPayments.push(pendingCashPayment);
	}

	async deletePendingCashPayment(id: string): Promise<void> {
		this.pendingCashPayments = this.pendingCashPayments.filter((p) => p.id !== id);
	}

	async createBillInsertion(billInsertion: BillInsertion): Promise<void> {
		this.billInsertions.push(billInsertion);
	}

	async processPendingCashPayments(sessionId: string | null): Promise<void> {
		const pendingPaymentsToDelete: PendingCashPayment[] = [];
		const pendingPayments = this.pendingCashPayments.filter((p) => !sessionId || p.sessionId !== sessionId);
		if (pendingPayments.length > 0) {
			KioAppHelper(
				'LogWarn',
				`Recovering ${pendingPayments.length} pending cash payment(s); currentSessionId=${sessionId ?? '(empty)'}, ` +
					`paymentSessionIds=${pendingPayments.map((p) => p.sessionId).join(',')}`
			);
		}
		for (const pendingPayment of pendingPayments) {
			const billInsertions = this.billInsertions.filter((b) => b.pendingCashPaymentId === pendingPayment.id);
			let ones = 0;
			let twos = 0;
			let fives = 0;
			let tens = 0;
			let twenties = 0;
			let fifties = 0;
			let hundreds = 0;
			let unknownCount = 0;
			let unknownAmount = 0;
			let amountToPay = 0;
			for (const insertion of billInsertions) {
				const amount = insertion.amount;
				if (amount === 1) ones++;
				else if (amount === 2) twos++;
				else if (amount === 5) fives++;
				else if (amount === 10) tens++;
				else if (amount === 20) twenties++;
				else if (amount === 50) fifties++;
				else if (amount === 100) hundreds++;
				else {
					unknownCount++;
					unknownAmount += amount;
					continue;
				}

				amountToPay += amount;
			}

			const requestData: CashPaymentData = new CashPaymentData({
				id: pendingPayment.id,
				sessionId: pendingPayment.sessionId,
				companyId: pendingPayment.companyId,
				kioskId: pendingPayment.kioskId,
				customerName: pendingPayment.customerName,
				utilityAccountNumber: pendingPayment.utilityAccountNumber,
				amountDue: pendingPayment.amountDue,
				amountToPay: amountToPay,
				legacyGuid: pendingPayment.legacyGuid,
				paymentGuid: pendingPayment.paymentGuid,
				cisId: pendingPayment.cisId,
				paymentGatewayId: pendingPayment.paymentGatewayId,
				paymentGatewayExternalId: pendingPayment.paymentGatewayExternalId,
				cisProviderName: pendingPayment.cisProviderName,
				paymentGatewayName: pendingPayment.paymentGatewayName,
				ones: ones,
				twos: twos,
				fives: fives,
				tens: tens,
				twenties: twenties,
				fifties: fifties,
				hundreds: hundreds,
				unknownCount: unknownCount,
				unknownAmount: unknownAmount,
				timestamp: new Date().toISOString(),
				paymentReference: pendingPayment.paymentReference,
			});

			const apiCall: ApiCall = {
				id: ulid().toUpperCase(),
				timestamp: new Date(),
				type: ApiCallServiceType.PostCashPayment,
				requestData: JSON.stringify(requestData),
			};

			this.apiCalls.push(apiCall);
			this.billInsertions = this.billInsertions.filter((b) => b.pendingCashPaymentId !== pendingPayment.id);
		}

		this.pendingCashPayments = this.pendingCashPayments.filter((p) => !pendingPaymentsToDelete.includes(p));
	}
}

const useSqliteEnv = import.meta.env.VITE_USE_SQLITE;
const _window = typeof self !== 'undefined' ? self : window;
const sqlite = useSqliteEnv === 'always' || (useSqliteEnv === 'with-fallback' && _window.KioSqLite) ? new SqliteService() : new SqliteServiceStub();
export default sqlite;
